import type { ComponentProps } from 'react';
import React from 'react';
import { upperFirst } from 'lodash-es';
import { Controller, useFormContext } from 'react-hook-form';
import type { AnySchema } from 'yup';
import { object, string } from 'yup';
import { OverridableSelect } from '../../../form/OverridableSelect';
import TimePicker from '../../../form/TimePicker';
import { useProfile } from '../../../../profile';
import { timeToDate } from '../../../../utils/time';

import arrowBentDown from './arrow-bent-down.svg';
import type { DaySchedule, PROJECT_OPTION_TYPE, Schedule, WeekDay } from './use-schedule-manager';
import { getPresetForDaySchedule, Measure, useScheduleManager } from './use-schedule-manager';
import FormFieldContainer from '../FormFieldContainer';
import { useScheduleRow } from './use-schedule-row';
import { PROJECT_OPTION } from '../ChoiceField';
import { useNestedFields } from '../../nested-field-registry';
import type { FieldOfName } from '../../../pages/measuringPoint/types';

type ControllerRules = NonNullable<ComponentProps<typeof Controller>['rules']>;

type ControllerRulesWithSchema = ControllerRules & {
    buildValidationSchema: (baseSchema: AnySchema) => AnySchema;
};

function ScheduleRow({
    name: scheduleFieldName,
    weekDay,
    displayName,
    isLast,
    is24Hour,
    schedule,
    onCopyToNextDay,
    onScheduleChange,
    error,
    nestedFields,
}: ScheduleRowProps) {
    const name = `${scheduleFieldName}.${weekDay}`;
    const { preset, presetValue, handlePresetChange, presetOptions, overridable, overridden } =
        useScheduleRow({
            schedule,
            weekDay,
            onScheduleChange,
            name,
        });

    const { ref } = nestedFields.register(`[${weekDay}]`);

    return (
        <div className="flex w-full flex-col gap-1">
            <div className="flex w-full flex-wrap gap-5">
                <div className="flex w-64 min-w-[200px] flex-shrink-0 flex-col">
                    <label className="mb-1 text-xs">{gettext(displayName)}</label>
                    <OverridableSelect
                        overridable={overridable}
                        overridden={overridden}
                        overrideName={name}
                        aria-label={weekDay ?? ''}
                        value={presetValue}
                        onChange={(event) => {
                            const selected = event.target.value;
                            if (selected) {
                                handlePresetChange(selected as Measure | PROJECT_OPTION_TYPE);
                            }
                        }}
                        data={presetOptions}
                        ref={ref}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <div className="flex flex-shrink-0 gap-5">
                        <div className="flex w-28 min-w-[100px] flex-shrink-0 flex-col">
                            <label className="mb-1 text-xs">{`${gettext('FROM')}:`}</label>
                            <TimePicker
                                onChange={(newValue) => {
                                    if (newValue !== null) onScheduleChange({ enable: newValue });
                                }}
                                value={schedule.enable && timeToDate(schedule.enable)}
                                disabled={
                                    preset !== Measure.CUSTOM || presetValue === PROJECT_OPTION
                                }
                                ariaLabel={`${weekDay}-enable`}
                                is24Hour={is24Hour}
                                error={Boolean(error)}
                            />
                        </div>

                        <div className="flex w-28 min-w-[100px] flex-shrink-0 flex-col">
                            <label className="mb-1 text-xs">{`${gettext('UNTIL')}:`}</label>
                            <TimePicker
                                onChange={(newValue) => {
                                    if (newValue !== null) onScheduleChange({ disable: newValue });
                                }}
                                value={schedule.disable && timeToDate(schedule.disable)}
                                disabled={
                                    preset !== Measure.CUSTOM || presetValue === PROJECT_OPTION
                                }
                                ariaLabel={`${weekDay}-disable`}
                                is24Hour={is24Hour}
                                error={Boolean(error)}
                            />
                        </div>
                    </div>

                    {error?.message && <div className="text-error text-xs">{error.message}</div>}
                </div>

                <div className="relative w-6 flex-shrink-0 self-stretch">
                    {!isLast && (
                        <button
                            title={gettext(upperFirst(weekDay))}
                            type="button"
                            onClick={onCopyToNextDay}
                            disabled={!!error}
                            className={`absolute -bottom-6 z-10 h-12 w-6 transition-transform hover:scale-105 ${
                                error
                                    ? 'cursor-not-allowed opacity-20'
                                    : 'cursor-pointer opacity-30'
                            }`}
                        >
                            <img src={arrowBentDown} className="max-w-5 opacity-30" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

interface ScheduleRowProps {
    schedule: DaySchedule;
    name: string;
    weekDay: WeekDay;
    displayName: string;
    onScheduleChange: (schedule: Partial<DaySchedule>) => void;
    onCopyToNextDay: () => void;
    is24Hour?: boolean;
    isLast?: boolean;
    error?: { message?: string };
    nestedFields: ReturnType<typeof useNestedFields>;
}

function ScheduleTable({ name, schedule, onChange, is24Hour, error }: ScheduleTableProps) {
    const { copyToNextDay, updateWeekday, week, lastDOW } = useScheduleManager(
        name,
        schedule,
        onChange
    );

    const nestedFields = useNestedFields(name);

    return (
        // The ID is needed for the walkthrough.
        <div className="flex flex-col text-gray-500" id="measurement_schedule">
            {week.map(({ weekDay, displayName }) => (
                <FormFieldContainer
                    fieldOptions={{ name }}
                    key={weekDay}
                    showOverrideCheckbox={false}
                >
                    <ScheduleRow
                        name={name}
                        schedule={schedule[weekDay] || {}}
                        weekDay={weekDay}
                        displayName={displayName}
                        is24Hour={is24Hour}
                        onCopyToNextDay={() => copyToNextDay(weekDay)}
                        onScheduleChange={(daySchedule) => {
                            updateWeekday(weekDay, daySchedule);
                        }}
                        isLast={weekDay === lastDOW}
                        error={error?.[weekDay]}
                        nestedFields={nestedFields}
                    />
                </FormFieldContainer>
            ))}
        </div>
    );
}

interface ScheduleTableProps {
    name: string;
    schedule: Schedule;
    onChange: (schedule: Schedule) => void;
    is24Hour?: boolean;
    error?: Record<string, { message?: string }>;
}

export function ScheduleField({ fieldOptions }: ScheduleFieldProps) {
    const { name, settings } = fieldOptions;
    const { use24HourNotation } = useProfile();
    const defaultSchedule = settings.default;
    const { control } = useFormContext();
    const scheduleValidationRules: ControllerRulesWithSchema = {
        buildValidationSchema() {
            return object(
                Object.fromEntries(
                    (Object.keys(defaultSchedule) as WeekDay[]).map((day) => [
                        day,
                        object({
                            enable: string(),
                            disable: string(),
                        }).test(
                            'schedule',
                            gettext('END_BEFORE_START_ERROR'),
                            function validateSchedule(value?: Partial<DaySchedule> | null) {
                                if (!value?.enable || !value?.disable) {
                                    return true;
                                }

                                const preset = getPresetForDaySchedule(value as DaySchedule);
                                if (preset !== Measure.CUSTOM) return true;
                                if (timeToDate(value.enable) < timeToDate(value.disable)) {
                                    return true;
                                }

                                return this.createError({
                                    path: this.path,
                                    message: gettext('END_BEFORE_START_ERROR'),
                                });
                            }
                        ),
                    ])
                )
            );
        },
    };

    return (
        <Controller
            name={name}
            control={control}
            defaultValue={defaultSchedule}
            rules={scheduleValidationRules}
            render={({ field: { value, onChange }, fieldState: { error } }) => (
                <ScheduleTable
                    name={name}
                    schedule={value as Schedule}
                    onChange={onChange}
                    is24Hour={use24HourNotation}
                    error={error as Record<string, { message: string }> | undefined}
                />
            )}
        />
    );
}

interface ScheduleFieldProps {
    fieldOptions: {
        name: string;
        settings: FieldOfName<'schedule'>;
    };
}
